import * as React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

// Components
import { ContentContainer } from '../../components/Layout/ContentContainer';
import Logo from '../../components/Logo';
import MenuOpener from '../../components/MenuOpener';
import TextBlock from '../../components/TextBlock';
import Title, { Subtext } from '../../components/Title';
import Footer from '../../components/Layout/Footer';
import SealAnimation from '../../components/SealAnimation';

// Assets
import { brandColors, device } from '../../styles/variables';
import blueLogo from '../../assets/images/logos/rd-logo-dark.png';
import Hyperlink from '../../components/Hyperlink';

const Wrapper = styled.div`
  background: ${brandColors.white};
`;

const ContentWrapper = styled.div`
  padding: 0 0 150px 0;
`;

const TitleContainer = styled.div`
  position: relative;
  margin: 154px 0 0 0;
`;

const SealContainer = styled.div`
  margin-top: 28px;

  @media ${device.tablet} {
    margin: 0;
    position: absolute;
    right: 100px;
    top: 0;
  }
`;

const Privacy = () => (
  <Wrapper>
    <MenuOpener className="menu-opener" />
    <Logo logo={blueLogo} />
    <ContentWrapper>
      <ContentContainer>
        <TitleContainer>
          <Title subtitle="geeky legals" color={brandColors.blue}>
            Privacy
            <SealContainer>
              <SealAnimation />
            </SealContainer>
            <Subtext width="636px">
              We are committed to data security and the fair and transparent processing of personal
              data.
            </Subtext>
          </Title>
        </TitleContainer>
        <TextBlock width="590px">
          <p>__</p>
          <h2>Owner and Data Controller</h2>
          <h3>Radical Departures Ltd.</h3>
          <p>
            The Printworks, 7 Blake Mews,<br />Kew, London, TW9 3GA<br />
            <Hyperlink
              originalColor={brandColors.blue}
              color={brandColors.orange}
              linkName="020&nbsp;8334&nbsp;7860"
              linkAddress="tel:02083347860"
            />
          </p>
          <p>__</p>

          <h2>Types of Data collected</h2>
          <p>
            Among the types of Personal Data that Radical Departures Ltd. collects, by itself or
            through third parties, there are: Cookies, Usage Data, first name, last name, profession
            and email address.
          </p>
          <p>
            Complete details on each type of Personal Data collected are provided in the dedicated
            sections of this privacy policy or by specific explanation texts displayed prior to the
            Data collection.
          </p>
          <p>
            Personal Data may be freely provided by the User, or, in case of Usage Data, collected
            automatically when using Radical Departures Ltd.
          </p>
          <p>
            Unless specified otherwise, all Data requested by Radical Departures Ltd. is mandatory
            and failure to provide this Data may make it impossible for Radical Departures Ltd. to
            provide its services. In cases where Radical Departures Ltd. specifically states that
            some Data is not mandatory, Users are free not to communicate this Data without
            consequences to the availability or the functioning of the Service.
          </p>
          <p>
            Users who are uncertain about which Personal Data is mandatory are welcome to contact
            the Owner.
          </p>
          <p>
            Any use of Cookies – or of other tracking tools – by Radical Departures Ltd. or by the
            owners of third-party services used by Radical Departures Ltd. serves the purpose of
            providing the Service required by the User, in addition to any other purposes described
            in the present document and in the Cookie Policy, if available.
          </p>
          <p>
            Users are responsible for any third-party Personal Data obtained, published or shared
            through Radical Departures Ltd. and confirm that they have the third party&apos;s
            consent to provide the Data to the Owner.
          </p>
        </TextBlock>
        <TextBlock width="590px">
          <p>__</p>
          <h2>Mode and place of processing the Data</h2>
          <h3>Methods of processing</h3>
          <p>
            The Owner takes appropriate security measures to prevent unauthorised access,
            disclosure, modification, or unauthorised destruction of the Data.
          </p>
          <p>
            The Data processing is carried out using computers and/or IT enabled tools, following
            organisational procedures and modes strictly related to the purposes indicated. In
            addition to the Owner, in some cases, the Data may be accessible to certain types of
            persons in charge, involved with the operation of Radical Departures Ltd.
            (administration, sales, marketing, legal, system administration) or external parties
            (such as third-party technical service providers, mail carriers, hosting providers, IT
            companies, communications agencies) appointed, if necessary, as Data Processors by the
            Owner. The updated list of these parties may be requested from the Owner at any time.
          </p>
          <br />
          <h2>Legal basis of processing</h2>
          <p>
            The Owner may process Personal Data relating to Users if one of the following applies:
          </p>
          <ul>
            <li>
              Users have given their consent for one or more specific purposes. Note: Under some
              legislations the Owner may be allowed to process Personal Data until the User objects
              to such processing (“opt-out”), without having to rely on consent or any other of the
              following legal bases. This, however, does not apply, whenever the processing of
              Personal Data is subject to European data protection law;
            </li>
            <li>
              provision of Data is necessary for the performance of an agreement with the User
              and/or for any pre-contractual obligations thereof;
            </li>
            <li>
              processing is necessary for compliance with a legal obligation to which the Owner is
              subject;
            </li>
            <li>
              processing is related to a task that is carried out in the public interest or in the
              exercise of official authority vested in the Owner;
            </li>
            <li>
              processing is necessary for the purposes of the legitimate interests pursued by the
              Owner or by a third party.
            </li>
          </ul>
          <p>
            In any case, the Owner will gladly help to clarify the specific legal basis that applies
            to the processing, and in particular whether the provision of Personal Data is a
            statutory or contractual requirement, or a requirement necessary to enter into a
            contract.
          </p>

          <h3>Place</h3>
          <p>
            The Data is processed at the Owner&apos;s operating offices and in any other places
            where the parties involved in the processing are located.
          </p>
          <p>
            Depending on the User&apos;s location, data transfers may involve transferring the
            User&apos;s Data to a country other than their own. To find out more about the place of
            processing of such transferred Data, Users can check the section containing details
            about the processing of Personal Data.
          </p>
          <p>
            Users are also entitled to learn about the legal basis of Data transfers to a country
            outside the European Union or to any international organization governed by public
            international law or set up by two or more countries, such as the UN, and about the
            security measures taken by the Owner to safeguard their Data.
          </p>
          <p>
            If any such transfer takes place, Users can find out more by checking the relevant
            sections of this document or enquire with the Owner using the information provided in
            the contact section.
          </p>

          <h3>Retention time</h3>
          <p>
            Personal Data shall be processed and stored for as long as required by the purpose they
            have been collected for.
          </p>
          <p>Therefore:</p>
          <ul>
            <li>
              Personal Data collected for purposes related to the performance of a contract between
              the Owner and the User shall be retained until such contract has been fully performed.
            </li>
            <li>
              Personal Data collected for the purposes of the Owner’s legitimate interests shall be
              retained as long as needed to fulfil such purposes. Users may find specific
              information regarding the legitimate interests pursued by the Owner within the
              relevant sections of this document or by contacting the Owner.
            </li>
          </ul>
          <p>
            The Owner may be allowed to retain Personal Data for a longer period whenever the User
            has given consent to such processing, as long as such consent is not withdrawn.
            Furthermore, the Owner may be obliged to retain Personal Data for a longer period
            whenever required to do so for the performance of a legal obligation or upon order of an
            authority.
          </p>
          <p>
            Once the retention period expires, Personal Data shall be deleted. Therefore, the right
            to access, the right to erasure, the right to rectification and the right to data
            portability cannot be enforced after expiration of the retention period.
          </p>

          <p>--</p>

          <h2>The purposes of processing</h2>
          <p>
            The Data concerning the User is collected to allow the Owner to provide its Services, as
            well as for the following purposes: Analytics and Contacting the User.
          </p>
          <p>
            Users can find further detailed information about such purposes of processing and about
            the specific Personal Data used for each purpose in the respective sections of this
            document.
          </p>

          <p>--</p>

          <h2>Detailed information on the processing of Personal Data</h2>
          <p>
            Personal Data is collected for the following purposes and using the following services:
          </p>

          <h3>Google Analytics (Google Ireland Limited)</h3>
          <p>
            Google Analytics is a web analysis service provided by Google Ireland Limited
            (“Google”). Google utilises the Data collected to track and examine the use of Radical
            Departures Ltd., to prepare reports on its activities and share them with other Google
            services.
          </p>
          <p>
            Google may use the Data collected to contextualise and personalise the ads of its own
            advertising network.
          </p>
          <p>Personal Data collected: Cookies and Usage Data.</p>
          <p>
            Place of processing: United States –{' '}
            <Hyperlink
              linkAddress="https://policies.google.com/privacy?hl=en"
              linkName="Privacy Policy"
              originalColor={brandColors.blue}
              color={brandColors.orange}
              constantLink
            />{' '}
            –{' '}
            <Hyperlink
              linkAddress="https://tools.google.com/dlpage/gaoptout?hl=en"
              linkName="Opt Out"
              originalColor={brandColors.blue}
              color={brandColors.orange}
              constantLink
            />{' '}
            . Privacy Shield participant.
          </p>

          <h3>Contact form (Radical Departures Ltd.)</h3>
          <p>
            By filling in the contact form with their Data, the User authorises Radical Departures
            Ltd. to use these details to reply to requests for information, quotes or any other kind
            of request as indicated by the form’s header.
          </p>
          <p>
            Personal Data collected: email address, first name, last name and professional
            information.
          </p>

          <p>--</p>

          <h2>The rights of Users</h2>
          <p>Users may exercise certain rights regarding their Data processed by the Owner.</p>
          <p>In particular, Users have the right to do the following:</p>
          <ul>
            <li>
              <b>Withdraw their consent at any time.</b> Users have the right to withdraw consent
              where they have previously given their consent to the processing of their Personal
              Data.
            </li>
            <li>
              <b>Object to processing of their Data.</b> Users have the right to object to the
              processing of their Data if the processing is carried out on a legal basis other than
              consent. Further details are provided in the dedicated section below.
            </li>
            <li>
              <b>Access their Data.</b> Users have the right to learn if Data is being processed by
              the Owner, obtain disclosure regarding certain aspects of the processing and obtain a
              copy of the Data undergoing processing.
            </li>
            <li>
              <b>Verify and seek rectification.</b> Users have the right to verify the accuracy of
              their Data and ask for it to be updated or corrected.
            </li>
            <li>
              <b>Restrict the processing of their Data.</b> Users have the right, under certain
              circumstances, to restrict the processing of their Data. In this case, the Owner will
              not process their Data for any purpose other than storing it.
            </li>
            <li>
              <b>Have their Personal Data deleted or otherwise removed.</b> Users have the right,
              under certain circumstances, to obtain the erasure of their Data from the Owner.
            </li>
            <li>
              <b>Receive their Data and have it transferred to another controller.</b> Users have
              the right to receive their Data in a structured, commonly used and machine-readable
              format and, if technically feasible, to have it transmitted to another controller
              without any hindrance. This provision is applicable provided that the Data is
              processed by automated means and that the processing is based on the User&apos;s
              consent, on a contract which the User is part of or on pre-contractual obligations
              thereof.
            </li>
            <li>
              <b>Lodge a complaint.</b> Users have the right to bring a claim before their competent
              data protection authority.
            </li>
          </ul>

          <h3>Details about the right to object to processing</h3>
          <p>
            Where Personal Data is processed for a public interest, in the exercise of an official
            authority vested in the Owner or for the purposes of the legitimate interests pursued by
            the Owner, Users may object to such processing by providing a ground related to their
            particular situation to justify the objection.
          </p>
          <p>
            Users must know that, however, should their Personal Data be processed for direct
            marketing purposes, they can object to that processing at any time without providing any
            justification. To learn, whether the Owner is processing Personal Data for direct
            marketing purposes, Users may refer to the relevant sections of this document.
          </p>

          <h3>How to exercise these rights</h3>
          <p>
            Any requests to exercise User rights can be directed to the Owner through the contact
            details provided in this document. These requests can be exercised free of charge and
            will be addressed by the Owner as early as possible and always within one month.
          </p>

          <p>--</p>

          <h2>Cookie Policy</h2>
          <p>
            Radical Departures Ltd. uses Cookies. To learn more and for a detailed cookie notice,
            the User may consult the{' '}
            <Hyperlink
              linkName="Cookie Policy"
              linkAddress="cookies"
              originalColor={brandColors.blue}
              color={brandColors.orange}
              constantLink
              internalLink
            />.
          </p>

          <p>--</p>

          <h2>Additional information about Data collection and processing</h2>
          <h3>Legal action</h3>
          <p>
            The User&apos;s Personal Data may be used for legal purposes by the Owner in Court or in
            the stages leading to possible legal action arising from improper use of Radical
            Departures Ltd. or the related Services.
          </p>
          <p>
            The User declares to be aware that the Owner may be required to reveal personal data
            upon request of public authorities.
          </p>

          <h3>Additional information about User&apos;s Personal Data</h3>
          <p>
            In addition to the information contained in this privacy policy, Radical Departures Ltd.
            may provide the User with additional and contextual information concerning particular
            Services or the collection and processing of Personal Data upon request.
          </p>

          <h3>System logs and maintenance</h3>
          <p>
            For operation and maintenance purposes, Radical Departures Ltd. and any third-party
            services may collect files that record interaction with Radical Departures Ltd. (System
            logs) use other Personal Data (such as the IP Address) for this purpose.
          </p>

          <h3>Information not contained in this policy</h3>
          <p>
            More details concerning the collection or processing of Personal Data may be requested
            from the Owner at any time. Please see the contact information at the beginning of this
            document.
          </p>

          <h3>How “Do Not Track” requests are handled</h3>
          <p>Radical Departures Ltd. does not support “Do Not Track” requests.</p>
          <p>
            To determine whether any of the third-party services it uses honour the “Do Not Track”
            requests, please read their privacy policies.
          </p>

          <h3>Changes to this privacy policy</h3>
          <p>
            The Owner reserves the right to make changes to this privacy policy at any time by
            giving notice to its Users on this page and possibly within Radical Departures Ltd.
            and/or - as far as technically and legally feasible - sending a notice to Users via any
            contact information available to the Owner. It is strongly recommended to check this
            page often, referring to the date of the last modification listed at the bottom.
          </p>
          <p>
            Should the changes affect processing activities performed on the basis of the User’s
            consent, the Owner shall collect new consent from the User, where required.
          </p>

          <p>--</p>

          <h2>Definitions and legal references</h2>

          <h3>Personal Data (or Data)</h3>
          <p>
            Any information that directly, indirectly, or in connection with other information —
            including a personal identification number — allows for the identification or
            identifiability of a natural person.
          </p>

          <h3>Usage Data</h3>
          <p>
            Information collected automatically through Radical Departures Ltd. (or third-party
            services employed in Radical Departures Ltd.), which can include: the IP addresses or
            domain names of the computers utilised by the Users who use Radical Departures Ltd., the
            URI addresses (Uniform Resource Identifier), the time of the request, the method
            utilised to submit the request to the server, the size of the file received in response,
            the numerical code indicating the status of the server&apos;s answer (successful
            outcome, error, etc.), the country of origin, the features of the browser and the
            operating system utilised by the User, the various time details per visit (e.g., the
            time spent on each page within the Application) and the details about the path followed
            within the Application with special reference to the sequence of pages visited, and
            other parameters about the device operating system and/or the User&apos;s IT
            environment.
          </p>

          <h3>User</h3>
          <p>
            The individual using Radical Departures Ltd. who, unless otherwise specified, coincides
            with the Data Subject.
          </p>

          <h3>Data Subject</h3>
          <p>The natural person to whom the Personal Data refers.</p>

          <h3>Data Processor (or Data Supervisor)</h3>
          <p>
            The natural or legal person, public authority, agency or other body which processes
            Personal Data on behalf of the Controller, as described in this privacy policy.
          </p>

          <h3>Data Controller (or Owner)</h3>
          <p>
            The natural or legal person, public authority, agency or other body which, alone or
            jointly with others, determines the purposes and means of the processing of Personal
            Data, including the security measures concerning the operation and use of Radical
            Departures Ltd. The Data Controller, unless otherwise specified, is the Owner of Radical
            Departures Ltd.
          </p>

          <h3>Radical Departures Ltd. (or this Application)</h3>
          <p>The means by which the Personal Data of the User is collected and processed.</p>

          <h3>Service</h3>
          <p>
            The service provided by Radical Departures Ltd. as described in the relative terms (if
            available) and on this site/application.
          </p>

          <h3>European Union (or EU)</h3>
          <p>
            Unless otherwise specified, all references made within this document to the European
            Union include all current member states to the European Union and the European Economic
            Area.
          </p>

          <h3>Cookies</h3>
          <p>Small sets of data stored in the User&apos;s device.</p>

          <p>--</p>

          <h2>Legal information</h2>
          <p>
            This privacy statement has been prepared based on provisions of multiple legislations,
            including Art. 13/14 of Regulation (EU) 2016/679 (General Data Protection Regulation).
          </p>
          <p>
            This privacy policy relates solely to Radical Departures Ltd., if not stated otherwise
            within this document.
          </p>
        </TextBlock>
      </ContentContainer>
    </ContentWrapper>
    <Footer />
  </Wrapper>
);

export default connect()(Privacy);



// WEBPACK FOOTER //
// ./src/pages/Privacy/index.jsx